import { useEffect, useContext, memo } from "react";
import { useSession } from "next-auth/client";
import { useRouter } from "next/router";
import {
  Box,
  VStack,
  Flex,
  Icon,
  Button,
  Divider,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  Link,
  IconButton,
  Tooltip,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Text,
  useColorModeValue,
  ModalFooter,
} from "@chakra-ui/react";
import NextLink from "next/link";
import { FiHelpCircle, FiSettings } from "react-icons/fi";
import { AiOutlineDollar } from "react-icons/ai";
import LiteYouTubeEmbed from "react-lite-youtube-embed";
import { Account } from "./account";
import { firebaseClient } from "firebaseClient";
import { useFirebaseUser } from "utils/useFirebaseUser";
import { FaHandsHelping, FaUniversity } from "react-icons/fa";
import { BiBug } from "react-icons/bi";
import { BsHexagonFill } from "react-icons/bs";
import { TweetContext } from "../../context/tweetContext";
import { showThTokens } from "utils/helpers";
import { MainContext } from "../../context/mainContext";
import { RiUserSettingsLine } from "react-icons/ri";
import { getAccount, updateUser, getAuthUrl } from 'utils/sessionHelper';
import { NavItem } from "./navItem";
import { AccordianNavMemoed } from "./accordianNav";
import { CircleQuestionSVGIcon } from "./icons/CircleQuestionSVGIcon";
import { CircleInfoSVGIcon } from "./icons/CircleInfoSVGIcon";
import { HeadsetSVGIcon } from "./icons/HeadsetSVGIcon";
import { WalkThroughSVGIcon } from "./icons/WalkThroughSVGIcon";
import { UniversitySVGIcon } from "./icons/UniversitySVGIcon";
import { CircleDollarSVGIcon } from "./icons/CircleDollarSVGIcon";

interface Props {
  navSize: "small" | "large";
  changeNavSize: (val: "small" | "large") => void;
  isMinimalist?: boolean;
  withShadowFooter?: boolean;
  expandedAccordionHelp, setExpandedAccordionHelp
}

export function Footer({
  navSize,
  changeNavSize,
  isMinimalist = false,
  withShadowFooter = false,
  expandedAccordionHelp, setExpandedAccordionHelp
}: Props) {
  const [session, loading] = useSession();
  const router = useRouter();
  const isAuthenticated = useFirebaseUser();
  const tweetContext: any = useContext(TweetContext);
  const mainContext: any = useContext(MainContext);
  const {
    isOpen: isOpenWalkthrough,
    onOpen: onOpenWalkthrough,
    onClose: onCloseWalkthrough,
  } = useDisclosure();
  const helpButtonHoverBg = useColorModeValue("gray.100", "gray.700");
  const helpButtonColor = useColorModeValue("gray.600", "gray.50");
  const universityIconColor = useColorModeValue("gray.600", "gray.50");
  const universityIconHoverColor = useColorModeValue("gray.100", "gray.700");
  const settingsIconColor = useColorModeValue("gray.600", "gray.50");
  const settingsIconBg = useColorModeValue("white", "#1E1E1E");
  const settingsIconHoverBg = useColorModeValue("gray.100", "gray.700");

  // useEffect(() => {
  //   if (
  //     isAuthenticated &&
  //     session?.user?.data?.needToShowWalkthrough &&
  //     (router.pathname.includes("auto") || router.pathname.includes("queue"))
  //   ) {
  //     onOpenWalkthrough();
  //     const db = firebaseClient.firestore();
  //     db.collection("users")
  //       .doc(session?.user?.uid)
  //       .update({ needToShowWalkthrough: false });
  //     session.user.data.needToShowWalkthrough = false;
  //   }
  // }, [session, isAuthenticated]);

  if (loading) {
    return null;
  }

  return (
    <>
      <Box
        {...{
          layerStyle:
            withShadowFooter && navSize === "large"
              ? "footerShadowTop"
              : undefined,
          borderRadius: "16px",
          overflow: "hidden",
        }}
      >
        <VStack
          {...{
            mx: navSize === "small" ? 0 : 3,
            mt: 3,
            spacing: 1,
          }}
        >
          <AccordianNavMemoed
            icon={CircleQuestionSVGIcon}
            iconExpanded={CircleQuestionSVGIcon}
            navSize={navSize}
            title="Learn / Help"
            {...{
              expanded: expandedAccordionHelp,
              setExpanded: setExpandedAccordionHelp,
            }}
            up
          >
            <VStack spacing="1">
              <NavItem
                title="Help Center"
                href="https://intercom.help/TaplioAndTweetHunter/en/collections/10397621-tweet-hunter"
                target="_blank"
                icon={CircleInfoSVGIcon}
                navSize={navSize}
                accordion
              />
              <NavItem
                title="Help Center & Support"
                onClick={() => {
                  try {
                    //@ts-ignore
                    window.Intercom("showNewMessage");
                  }
                  catch (e) {
                    console.log("error in intercom", e);
                  }
                }}
                display="block"
                icon={HeadsetSVGIcon}
                navSize={navSize}
                accordion
              />
              {/* <NavItem
                title="Support"
                onClick={() => {
                  Gleap.open();
                }}
                icon={HeadsetSVGIcon}
                navSize={navSize}
                accordion
              /> */}
              {/* <NavItem
                title="Walkthrough"
                onClick={() => {
                  onOpenWalkthrough();
                  changeNavSize("small");
                }}
                icon={WalkThroughSVGIcon}
                navSize={navSize}
                accordion
              /> */}
              <NavItem
                title="Tweet Hunter U"
                target="_blank"
                href="https://tweethunter.io/blog/tweet-hunter-university"
                icon={UniversitySVGIcon}
                navSize={navSize}
                accordion
              />
            </VStack>
          </AccordianNavMemoed>
          <NavItem
            title="Account Settings"
            href="/settings"
            icon={<svg width="14" height="14" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M5 8C5 6.34375 6.3125 5 8 5C9.65625 5 11 6.34375 11 8C11 9.65625 9.65625 11 8 11C6.3125 11 5 9.65625 5 8ZM8 6.5C7.15625 6.5 6.5 7.1875 6.5 8C6.5 8.84375 7.15625 9.5 8 9.5C8.8125 9.5 9.5 8.84375 9.5 8C9.5 7.1875 8.8125 6.5 8 6.5ZM9.15625 0C9.84375 0 10.4375 0.5 10.5938 1.15625L10.8438 2.1875C11.125 2.3125 11.375 2.46875 11.5938 2.625L12.625 2.3125C13.2812 2.125 14 2.40625 14.3438 3L15.5 5.03125C15.8438 5.625 15.7188 6.375 15.2188 6.84375L14.4688 7.5625C14.4688 7.71875 14.5 7.875 14.5 8C14.5 8.15625 14.4688 8.3125 14.4688 8.4375L15.2188 9.1875C15.7188 9.65625 15.8438 10.4062 15.5 11L14.3438 13.0312C14 13.625 13.2812 13.9062 12.625 13.7188L11.5938 13.4062C11.375 13.5625 11.125 13.7188 10.8438 13.8438L10.5938 14.875C10.4375 15.5312 9.84375 16 9.15625 16H6.8125C6.125 16 5.53125 15.5312 5.375 14.875L5.125 13.8438C4.84375 13.7188 4.59375 13.5625 4.375 13.4062L3.34375 13.7188C2.6875 13.9062 1.96875 13.625 1.625 13.0312L0.46875 11C0.125 10.4062 0.25 9.65625 0.75 9.1875L1.5 8.4375C1.5 8.3125 1.5 8.15625 1.5 8C1.5 7.875 1.5 7.71875 1.5 7.5625L0.75 6.84375C0.25 6.375 0.125 5.625 0.46875 5.03125L1.625 3C1.96875 2.40625 2.6875 2.125 3.34375 2.3125L4.375 2.625C4.59375 2.46875 4.84375 2.3125 5.125 2.1875L5.375 1.15625C5.53125 0.5 6.125 0 6.8125 0H9.15625ZM6.40625 3.25L6.0625 3.40625C5.65625 3.5625 5.28125 3.78125 4.96875 4.03125L4.65625 4.28125L2.9375 3.75L1.78125 5.78125L3.0625 7L3.03125 7.375C3 7.59375 3 7.8125 3 8C3 8.21875 3 8.4375 3.03125 8.65625L3.0625 9.03125L1.78125 10.25L2.9375 12.2812L4.65625 11.75L4.96875 12C5.28125 12.25 5.65625 12.4688 6.0625 12.625L6.40625 12.7812L6.8125 14.5H9.15625L9.5625 12.7812L9.90625 12.625C10.3125 12.4688 10.6875 12.25 11 12L11.3125 11.75L13.0312 12.2812L14.1875 10.25L12.9062 9.03125L12.9375 8.65625C12.9688 8.4375 13 8.21875 13 8C13 7.8125 12.9688 7.59375 12.9375 7.375L12.9062 7L14.1875 5.78125L13.0312 3.75L11.3125 4.28125L11 4.03125C10.6875 3.78125 10.3125 3.5625 9.90625 3.40625L9.5625 3.25L9.15625 1.5H6.8125L6.40625 3.25Z" fill="currentColor" />
            </svg>
            }
            // icon={<FiSettings fontSize="16px" />}
            navSize={navSize}
            iconStroke="1"
          />
          {/* <NavItem
            title="Become an affiliate"
            href="https://affiliates.tweethunter.io/"
            target="_blank"
            icon={
           
                CircleDollarSVGIcon
         
            }
            navSize={navSize}
            iconStroke="1"
            affiliate
          /> */}
          <Box {...{ h: 0 }} />
          {navSize === "small" && (
            <IconButton
              aria-label="affiliate"
              icon={CircleDollarSVGIcon}
              variant="primaryAffiliate"
              size="sm"
              opacity={0.3}
              _hover={{ opacity: 1 }}
            ></IconButton>
          )}
          {navSize === "large" && (
            <Button
              as={Link}
              href="https://tweethunter.io/affiliates"
              target="_blank"
              aria-label="affiliate"
              leftIcon={CircleDollarSVGIcon}
              variant="primaryAffiliate"
              // w={"calc(100% - 24px)"}
              w={"calc(100% + 8px)"}
              // w="fit-content"
              size="sm"
              // fontSize="xs"
              placeSelf="center"
              // placeSelf="start"
              textAlign="start"
              px="2.5"
              mx="8"
              py={1}
              opacity={0.3}
              _hover={{ opacity: 1, textDecoration: "none" }}

            >
              Become an affiliate
            </Button>
          )}
        </VStack>
      </Box>
      <Account navSize={navSize} />
      <Modal
        isOpen={isOpenWalkthrough}
        onClose={onCloseWalkthrough}
        size="4xl"
        isCentered
      >
        <ModalOverlay />
        <ModalContent boxShadow="rgba(100, 100, 100, 1) 0px 0px 30px 20px;">
          <ModalHeader>
            Don't start yet (or download our Twitter Growth guide below)
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody p={0}>
            <LiteYouTubeEmbed
              id="TprlA-lRX-k"
              title="Tweet Hunter walkthrough"
              announce="Watch"
            />
          </ModalBody>
          <ModalFooter>
            <Box p={5}>
              🔑{" "}
              <Link
                textDecoration="underline"
                as="a"
                href="https://tweethunter.io/university"
                target="_blank"
              >
                Tweet Hunter U (lifetime access)
              </Link>
            </Box>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
}

export const FooterMemoed = memo(Footer);
